/**
 * Alte Notizen in die Band-Bibel überführen.
 *
 * WARUM ES DIESE DATEI GIBT
 * -------------------------
 * Der Reiter "Notizen" ist aus der App verschwunden – die Band-Bibel
 * übernimmt diese Aufgabe. Die vorhandenen Notizen einfach mitzulöschen wäre
 * Datenverlust, und sie im alten Store liegen zu lassen hiesse, sie
 * unerreichbar zu machen. Also wandern sie um: je Notiz eine Fläche im
 * Systemraum "Allgemein", mit dem Text als Objekt darauf.
 *
 * WAS BEWUSST BLEIBT
 * ------------------
 * Der "notes"-Store, der Typ `Note` und die Backup-Felder bleiben erhalten.
 * Nur so lässt sich ein älteres Backup weiterhin einlesen – und dessen
 * Notizen werden beim nächsten Start ebenfalls überführt. Neue Notizen legt
 * die App nicht mehr an.
 *
 * Die Überführung läuft genau einmal je Notiz: die Herkunft steht auf der
 * Fläche, und die Notiz wird danach aus dem alten Store entfernt.
 */
import type { Board, Note } from '../types.ts';
import { STORE, getAll } from './idb.ts';
import {
  deleteNote,
  emptyBoard,
  emptyObject,
  noteTitle,
  saveBoard,
  systemRoom,
} from './repo.ts';

/** Breite und Höhe des Textobjekts, in das eine Notiz wandert. */
const CARD_W = 420;
const CARD_H = 320;
/** Ausgangspunkt auf der Fläche – etwas eingerückt, nicht in der Ecke. */
const START_X = 320;
const START_Y = 240;

export interface NoteMigration {
  /** Wie viele Notizen überführt wurden. */
  moved: number;
}

/**
 * Überführt alle noch vorhandenen Notizen eines Projekts.
 * Gibt es keine, passiert nichts – die Funktion darf bei jedem Start laufen.
 */
export async function migrateNotesToBible(projectId: string): Promise<NoteMigration> {
  const all = await getAll<Note>(STORE.notes);
  const mine = all.filter((note) => note.projectId === projectId);
  if (mine.length === 0) return { moved: 0 };

  const room = await systemRoom(projectId, 'general');
  let moved = 0;

  for (const note of mine) {
    // Eine leere Notiz ist nichts wert – die wird nur entfernt.
    if (note.body.trim() === '') {
      await deleteNote(note.id);
      continue;
    }

    const board: Board = emptyBoard(projectId, room.id, {
      title: noteTitle(note) || 'Notiz',
      // Zeitstempel mitnehmen, damit die Reihenfolge in der Liste stimmt.
      createdAt: note.createdAt,
      updatedAt: note.updatedAt,
      objects: [
        emptyObject({
          type: 'card',
          x: START_X,
          y: START_Y,
          w: CARD_W,
          h: CARD_H,
          text: note.body,
        }),
      ],
    });
    await saveBoard(board);
    await deleteNote(note.id);
    moved += 1;
  }

  return { moved };
}

/** Wie viele Notizen warten noch auf die Überführung? Für den Wartungsbereich. */
export async function pendingNoteCount(): Promise<number> {
  const all = await getAll<Note>(STORE.notes);
  return all.length;
}
